import { DOCUMENT } from '@angular/common';
import {
    DestroyRef,
    Injectable,
    computed,
    inject,
    signal,
} from '@angular/core';
import { AppTheme } from '@interfaces/app-theme.type';

const STORAGE_KEY = 'sensorika_theme_preference';

@Injectable({
    providedIn: 'root',
})
export class ThemeService {
    private readonly document = inject(DOCUMENT);
    private readonly destroyRef = inject(DestroyRef);
    private readonly mediaQuery = this.document.defaultView?.matchMedia(
        '(prefers-color-scheme: dark)',
    );
    private readonly systemTheme = signal<AppTheme>(
        this.mediaQuery?.matches ? 'dark' : 'light',
    );
    private readonly override = signal<AppTheme | null>(this.readOverride());
    readonly theme = computed<AppTheme>(() => this.override() ?? this.systemTheme());
    readonly isDark = computed(() => this.theme() === 'dark');

    private initialized = false;

    initialize(): void {
        if (this.initialized) return;
        this.initialized = true;

        this.applyTheme();
        this.mediaQuery?.addEventListener('change', this.onSystemThemeChange);
        this.destroyRef.onDestroy(() =>
            this.mediaQuery?.removeEventListener(
                'change',
                this.onSystemThemeChange,
            ),
        );
    }

    toggle(): void {
        const next: AppTheme = this.isDark() ? 'light' : 'dark';
        const nextOverride = next === this.systemTheme() ? null : next;
        this.override.set(nextOverride);

        if (nextOverride) {
            localStorage.setItem(STORAGE_KEY, nextOverride);
        } else {
            localStorage.removeItem(STORAGE_KEY);
        }

        this.applyTheme();
    }

    private readonly onSystemThemeChange = (
        event: MediaQueryListEvent,
    ): void => {
        this.systemTheme.set(event.matches ? 'dark' : 'light');

        if (this.override() === null) {
            this.applyTheme();
        }
    };

    private applyTheme(): void {
        const root = this.document.documentElement;
        root.classList.toggle('dark-theme', this.isDark());
        root.classList.toggle('light-theme', !this.isDark());
    }

    private readOverride(): AppTheme | null {
        const preference = localStorage.getItem(STORAGE_KEY);
        return preference === 'light' || preference === 'dark' ? preference : null;
    }
}
